import { useEffect, useState, useCallback } from 'react';
import {
  uploadsGallery,
  settings as settingsApi,
  type UserUpload,
  type UploadsStats,
} from '../../api/client';
import {
  Image as ImageIcon,
  Trash2,
  RefreshCw,
  AlertCircle,
  Search,
  X,
  CheckCircle2,
  Hash,
  HardDrive,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const PAGE_SIZE = 24;
const BULK_DELETE_CHUNK = 100;

export default function UploadsGallery() {
  const [uploads, setUploads] = useState<UserUpload[]>([]);
  const [stats, setStats] = useState<UploadsStats | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [tier, setTier] = useState('');

  // Detail view
  const [detail, setDetail] = useState<UserUpload | null>(null);

  // Bulk delete-all flow
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState('');
  const [deletingAll, setDeletingAll] = useState(false);

  // Kill-switch toggle state — sourced from /admin/uploads/stats so it's
  // consistent with what the proxy actually reads (settings table).
  const [retentionToggleBusy, setRetentionToggleBusy] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [listResponse, statsResponse] = await Promise.all([
        uploadsGallery.list({
          search: search || undefined,
          deviceId: deviceId || undefined,
          tier: tier || undefined,
          limit: PAGE_SIZE,
          offset,
        }),
        uploadsGallery.stats(),
      ]);
      setUploads(listResponse.uploads);
      setTotal(listResponse.total);
      setStats(statsResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load uploads');
    } finally {
      setLoading(false);
    }
  }, [search, deviceId, tier, offset]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-dismiss success banner after 3s
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 3000);
    return () => clearTimeout(t);
  }, [success]);

  const handleToggleRetention = async (next: boolean) => {
    setRetentionToggleBusy(true);
    try {
      await settingsApi.update({
        user_uploads_retention_enabled: next ? 'true' : 'false',
      });
      const refreshed = await uploadsGallery.stats();
      setStats(refreshed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update setting');
    } finally {
      setRetentionToggleBusy(false);
    }
  };

  const handleDeleteOne = async (upload: UserUpload) => {
    if (!confirm(`Delete this upload?\n\n${upload.r2_key}`)) return;
    try {
      await uploadsGallery.delete(upload.id);
      setDetail(null);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const handleDeleteAll = async () => {
    if (deleteAllConfirmText !== 'DELETE') return;
    setDeletingAll(true);
    setError(null);
    try {
      // Page through all id pages and bulk-delete in 100-id chunks.
      // Mirrors R2Gallery's client-side fan-out — no server-side
      // delete-all endpoint by design.
      let totalDeleted = 0;
      while (true) {
        const page = await uploadsGallery.list({
          deviceId: deviceId || undefined,
          tier: tier || undefined,
          search: search || undefined,
          limit: 500,
          offset: 0,
        });
        if (page.uploads.length === 0) break;
        const ids = page.uploads.map((u) => u.id);
        for (let i = 0; i < ids.length; i += BULK_DELETE_CHUNK) {
          const chunk = ids.slice(i, i + BULK_DELETE_CHUNK);
          const result = await uploadsGallery.bulkDelete(chunk);
          totalDeleted += result.r2Deleted;
        }
        if (page.uploads.length < 500) break;
      }
      setShowDeleteAllConfirm(false);
      setDeleteAllConfirmText('');
      setOffset(0);
      await fetchData();
      setSuccess(`Deleted ${totalDeleted.toLocaleString()} upload${totalDeleted === 1 ? '' : 's'}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete-all failed');
    } finally {
      setDeletingAll(false);
    }
  };

  const initialLoading = loading && uploads.length === 0 && stats === null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient">User Uploads</h1>
          <p className="text-muted-foreground mt-1">
            Image attachments saved during image-captioning chat completions. Bytes live in R2 under{' '}
            <code className="text-amber-400/70 font-mono">uploads/</code>.
          </p>
        </div>
        <button
          onClick={fetchData}
          className="btn-secondary px-4 py-2 rounded-xl flex items-center gap-2 text-sm"
          disabled={loading}
        >
          <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Success banner */}
      {success && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 flex items-center gap-3 animate-scale-in">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
          <span className="text-sm text-emerald-300 flex-1">{success}</span>
          <button
            onClick={() => setSuccess(null)}
            className="p-1 rounded-lg hover:bg-emerald-500/20 text-emerald-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4 flex items-center gap-3 animate-scale-in">
          <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0" />
          <span className="text-sm text-rose-300 flex-1">{error}</span>
          <button
            onClick={() => setError(null)}
            className="p-1 rounded-lg hover:bg-rose-500/20 text-rose-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Kill-switch toggle */}
      {stats && (
        <div className="card-glow rounded-2xl p-6 animate-fade-in-up animate-delay-100">
          <div className="flex items-center justify-between gap-6">
            <div>
              <div className="text-sm font-medium">Save uploads to R2 (debugging only)</div>
              <div className="text-xs text-muted-foreground mt-1">
                When OFF, the captioning request still flows through to the upstream provider — we just don't
                keep a record of the bytes. Existing uploads are not deleted by toggling this off.
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={stats.retentionEnabled}
              disabled={retentionToggleBusy}
              onClick={() => handleToggleRetention(!stats.retentionEnabled)}
              className={cn(
                'w-11 h-6 rounded-full transition-colors relative flex-shrink-0 disabled:opacity-50',
                stats.retentionEnabled ? 'bg-emerald-500' : 'bg-background border border-border'
              )}
            >
              <div
                className={cn(
                  'w-4 h-4 rounded-full bg-white absolute top-1 transition-all',
                  stats.retentionEnabled ? 'left-6' : 'left-1'
                )}
              />
            </button>
          </div>
        </div>
      )}

      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-fade-in-up animate-delay-200">
          <div className="stat-card-cyan card-glow p-5 rounded-2xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                <Hash className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total uploads</p>
                <p className="text-2xl font-bold">{stats.totalUploads.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="stat-card-emerald card-glow p-5 rounded-2xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <HardDrive className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total size</p>
                <p className="text-2xl font-bold">{stats.totalMB.toFixed(2)} MB</p>
              </div>
            </div>
          </div>
          <div className="stat-card-violet card-glow p-5 rounded-2xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-violet-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Retention</p>
                <p className="text-2xl font-bold">{stats.retentionEnabled ? 'ON' : 'OFF'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card-glow rounded-2xl p-4 animate-fade-in-up animate-delay-300">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search caption / user text"
              className="input-neural w-full pl-10 pr-4 py-2.5 rounded-xl text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setOffset(0);
                  fetchData();
                }
              }}
            />
          </div>
          <input
            type="text"
            placeholder="Device ID"
            className="input-neural px-4 py-2.5 rounded-xl text-sm font-mono w-48"
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setOffset(0);
                fetchData();
              }
            }}
          />
          <select
            className="input-neural px-4 py-2.5 rounded-xl text-sm appearance-none cursor-pointer"
            value={tier}
            onChange={(e) => {
              setTier(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">All tiers</option>
            <option value="standard">Standard</option>
            <option value="premium">Premium</option>
            <option value="subscriber">Subscriber</option>
            <option value="custom_fallback">Custom fallback</option>
          </select>
          <button
            onClick={() => setShowDeleteAllConfirm(true)}
            disabled={total === 0 || deletingAll}
            className="px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 className="w-4 h-4" />
            Delete all
          </button>
        </div>
      </div>

      {/* Grid + pagination */}
      {initialLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3 text-muted-foreground">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>Loading uploads...</span>
          </div>
        </div>
      ) : uploads.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 animate-fade-in-up">
          <div className="w-20 h-20 rounded-2xl bg-secondary flex items-center justify-center mb-4">
            <ImageIcon className="w-10 h-10 text-muted-foreground" />
          </div>
          <p className="text-muted-foreground mb-1">
            {stats?.retentionEnabled ? 'No uploads yet' : 'Retention is OFF'}
          </p>
          <p className="text-sm text-muted-foreground/70">
            {stats?.retentionEnabled
              ? 'Image-captioning requests will appear here'
              : 'No uploads are being saved'}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 animate-fade-in-up animate-delay-400">
            {uploads.map((upload, index) => (
              <button
                key={upload.id}
                onClick={() => setDetail(upload)}
                style={{ animationDelay: `${index * 30}ms` }}
                className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer card-glow animate-fade-in-up text-left"
              >
                {upload.r2_url ? (
                  <img
                    src={upload.r2_url}
                    alt={upload.caption ?? 'Upload'}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full bg-secondary flex items-center justify-center text-xs text-muted-foreground">
                    no preview
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 pt-6">
                  <p className="text-xs text-white/90 truncate">
                    {upload.tier || 'cloud'} · {new Date(upload.created_at).toLocaleDateString()}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-4 card-glow rounded-2xl animate-fade-in-up animate-delay-500">
            <div className="text-sm text-muted-foreground">
              Showing <span className="font-mono text-foreground">{offset + 1}</span>–
              <span className="font-mono text-foreground">
                {Math.min(offset + uploads.length, total)}
              </span>{' '}
              of <span className="font-mono text-foreground">{total.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
                className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= total}
                className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Detail dialog */}
      {detail && <UploadDetailModal upload={detail} onClose={() => setDetail(null)} onDelete={() => handleDeleteOne(detail)} />}

      {/* Delete-all confirm */}
      {showDeleteAllConfirm && (
        <div
          className="fixed inset-0 modal-overlay flex items-center justify-center z-50 p-4"
          onClick={() => !deletingAll && setShowDeleteAllConfirm(false)}
        >
          <div
            className="modal-content rounded-2xl max-w-md w-full p-6 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-rose-500/10 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-rose-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Delete all uploads?</h2>
                <p className="text-sm text-muted-foreground">This action is permanent</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              This permanently deletes <strong className="text-foreground">{total.toLocaleString()}</strong>{' '}
              uploaded image{total === 1 ? '' : 's'} from R2 and removes their D1 rows. Type{' '}
              <code className="text-amber-400/70 font-mono">DELETE</code> to confirm.
            </p>
            <input
              type="text"
              className="input-neural w-full px-4 py-3 rounded-xl text-sm font-mono mb-4"
              value={deleteAllConfirmText}
              onChange={(e) => setDeleteAllConfirmText(e.target.value)}
              placeholder="DELETE"
              autoFocus
              disabled={deletingAll}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDeleteAllConfirm(false);
                  setDeleteAllConfirmText('');
                }}
                disabled={deletingAll}
                className="btn-secondary px-4 py-2.5 rounded-xl text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAll}
                disabled={deleteAllConfirmText !== 'DELETE' || deletingAll}
                className="px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 bg-rose-600/30 text-rose-300 hover:bg-rose-600/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingAll ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Deleting…
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete all
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UploadDetailModal({
  upload,
  onClose,
  onDelete,
}: {
  upload: UserUpload;
  onClose: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      className="fixed inset-0 modal-overlay flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="modal-content rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden animate-scale-in flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header bar */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-border flex-shrink-0">
          <code className="text-xs font-mono text-muted-foreground truncate flex-1" title={upload.r2_key}>
            {upload.r2_key}
          </code>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg bg-secondary text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Image */}
        {upload.r2_url && (
          <div className="bg-black/50 flex items-center justify-center flex-shrink-0">
            <img
              src={upload.r2_url}
              alt={upload.caption ?? 'Upload'}
              className="max-w-full max-h-[50vh] object-contain"
            />
          </div>
        )}

        {/* Body — scrollable */}
        <div className="p-6 space-y-5 overflow-y-auto">
          {/* Caption */}
          <div>
            <p className="text-xs text-muted-foreground mb-1.5">Caption</p>
            <p className="text-sm whitespace-pre-wrap break-words text-foreground/90">
              {upload.caption || <span className="text-muted-foreground italic">(none yet)</span>}
            </p>
          </div>

          {/* User text */}
          <div>
            <p className="text-xs text-muted-foreground mb-1.5">User text</p>
            <p className="text-sm whitespace-pre-wrap break-words text-foreground/90">
              {upload.user_text || <span className="text-muted-foreground italic">(empty)</span>}
            </p>
          </div>

          {/* Metadata grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="p-3 rounded-xl bg-secondary/30">
              <p className="text-xs text-muted-foreground mb-1">Tier</p>
              <p className="text-sm text-foreground/90">{upload.tier ?? '(unset)'}</p>
            </div>
            <div className="p-3 rounded-xl bg-secondary/30">
              <p className="text-xs text-muted-foreground mb-1">Dimensions</p>
              <p className="font-mono text-sm text-foreground/90">
                {upload.width && upload.height ? `${upload.width}×${upload.height}` : '(unknown)'}
              </p>
            </div>
            <div className="p-3 rounded-xl bg-secondary/30">
              <p className="text-xs text-muted-foreground mb-1">Size</p>
              <p className="font-mono text-sm text-foreground/90">
                {upload.size_bytes ? `${(upload.size_bytes / 1024).toFixed(1)} KB` : '(unknown)'}
              </p>
            </div>
            <div className="p-3 rounded-xl bg-secondary/30">
              <p className="text-xs text-muted-foreground mb-1">MIME</p>
              <p className="font-mono text-sm text-foreground/90">{upload.mime_type ?? '(unknown)'}</p>
            </div>
            <div className="p-3 rounded-xl bg-secondary/30 md:col-span-2">
              <p className="text-xs text-muted-foreground mb-1">Created</p>
              <p className="text-sm text-foreground/90">{new Date(upload.created_at).toLocaleString()}</p>
            </div>
            <div className="p-3 rounded-xl bg-secondary/30 md:col-span-3">
              <p className="text-xs text-muted-foreground mb-1">Device</p>
              <p className="font-mono text-xs text-foreground/90 break-all">{upload.device_id}</p>
            </div>
            {upload.conversation_id && (
              <div className="p-3 rounded-xl bg-secondary/30 md:col-span-3">
                <p className="text-xs text-muted-foreground mb-1">Conversation</p>
                <p className="font-mono text-xs text-foreground/90 break-all">{upload.conversation_id}</p>
              </div>
            )}
          </div>
        </div>

        {/* Actions footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border flex-shrink-0">
          <button onClick={onClose} className="btn-secondary px-4 py-2 rounded-xl text-sm">
            Close
          </button>
          <button
            onClick={onDelete}
            className="px-4 py-2 rounded-xl text-sm flex items-center gap-2 bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
